import DateRangeIcon from '@mui/icons-material/DateRange';
import KeyArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import QButton from 'components/QButton';
import { DateTime, Interval } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useReducer } from 'react';
import CustomActionbar from './Actionbar';
import CustomLayout from './Layout';
import CustomShortcuts from './Shortcuts';
import ShortcutsSelect from './ShortcutsSelect';
import CustomToolbar from './Toolbar';
import useStyles from './styles';

const getDisplayedRangeForId = (ranges, selectedRangeId) =>
  ranges?.find((item) => item.id === selectedRangeId)?.label || 'Custom';

const getDatesForId = (map, id) => {
  const item = map.get(id);
  if (item?.getValue) {
    return item.getValue();
  }
  if (item?.value && item?.value?.isLuxonInterval) {
    return [item.value.start, item.value.end];
  }
  return null;
};

const getRangeForId = (id, ranges) => id && ranges && ranges.find((range) => range?.id === id);

const getIdForRange = (ranges, selectedStartDate, selectedEndDate) => {
  const selectedInterval = Interval.fromDateTimes(selectedStartDate, selectedEndDate);

  const found = ranges.find((item) => {
    let shortcutInterval;
    if (item?.getValue) {
      const [start, end] = item.getValue();
      shortcutInterval = Interval.fromDateTimes(start, end);
    } else if (item?.value?.isLuxonInterval) {
      shortcutInterval = item.value;
    }

    return shortcutInterval && shortcutInterval.equals(selectedInterval);
  });

  return found?.id || null;
};

const initState = ({
  startingDate,
  endingDate,
  ranges,
  selectedRangeId,
  defaultRangeId,
  maxDaysBefore,
  maxDaysAfter,
}) => {
  const [rangeStart, rangeEnd] = getRangeForId(selectedRangeId, ranges)?.getValue?.() || [undefined, undefined];
  const startDate = startingDate || rangeStart || DateTime.local();
  const endDate = endingDate || rangeEnd || DateTime.local().plus({ day: 30 });
  const predefinedRanges = ranges || [];
  const shortCutsMap = ranges ? new Map(ranges.map((i) => [i.id, i])) : new Map();
  const selectedShortcutId = selectedRangeId || getIdForRange(predefinedRanges, startDate, endDate);
  const displayedRange = getDisplayedRangeForId(ranges, selectedShortcutId);
  const selectedDefaultId = defaultRangeId || null;
  const rangePosition = 'start'; // 'start' or 'end' depending on which date is to be set
  const minDate = maxDaysBefore >= 0 ? DateTime.local().startOf('day').minus({ day: maxDaysBefore }) : null;
  const maxDate = maxDaysAfter >= 0 ? DateTime.local().endOf('day').plus({ day: maxDaysAfter }) : null;

  return {
    startDate,
    endDate,
    predefinedRanges,
    shortCutsMap,
    selectedShortcutId,
    displayedRange,
    selectedDefaultId,
    rangePosition,
    minDate,
    maxDate,
    errors: { isValidRange: true },
    anchorEl: null,
  };
};

function verifyRange(minDate, maxDate, selectedStart, selectedEnd) {
  const returnedErrors = {};

  // startDate Errors
  if (!selectedStart || selectedStart?.invalid) {
    returnedErrors.startHelperText = 'Input error: date is not valid';
  } else if (minDate && selectedStart < minDate) {
    returnedErrors.startHelperText = `Out of range: select a date on or after ${minDate.toLocaleString()}`;
  } else if (maxDate && selectedStart > maxDate) {
    returnedErrors.startHelperText = `Out of range: select a date on or before ${maxDate.toLocaleString()}`;
  }

  // endDate Errors
  if (!selectedEnd || selectedEnd?.invalid) {
    returnedErrors.endHelperText = 'Input error: date is not valid';
  } else if (maxDate && selectedEnd > maxDate) {
    returnedErrors.endHelperText = `Out of range: select a date on or before ${maxDate.toLocaleString()}`;
  } else if (minDate && selectedEnd < minDate) {
    returnedErrors.endHelperText = `Out of range: select a date on or after ${minDate.toLocaleString()}`;
  }

  if (selectedEnd < selectedStart) {
    returnedErrors.endHelperText = 'Invalid: End date must be on or after Start date';
  }

  returnedErrors.isValidRange = !(
    returnedErrors.startHelperText ||
    returnedErrors.endHelperText ||
    selectedStart === null ||
    selectedEnd === null
  );

  return returnedErrors;
}

function getSelectValuesForDates(predefinedRanges, startDate, endDate) {
  // if one of the dates is missing  - this is not valid and can't be a shortcut
  if (!startDate || !endDate) {
    return { startDate, endDate, selectedShortcutId: null, displayedRange: null };
  }

  // both dates were provided, try to find a matching shortcut and set the displayRange
  if (startDate && endDate) {
    const selectedShortcutId = getIdForRange(predefinedRanges, startDate, endDate) || null;
    const displayedRange = selectedShortcutId ? getDisplayedRangeForId(predefinedRanges, selectedShortcutId) : 'Custom';
    return { startDate, endDate, selectedShortcutId, displayedRange };
  }

  return {};
}

function reducer(state, action) {
  switch (action.type) {
    case 'calendarDisplayed':
      return { ...state, anchorEl: action.payload };
    case 'cancel':
      return { ...action.payload };
    case 'shortcutSelected': {
      const errors = verifyRange(state.minDate, state.maxDate, action.payload.startDate, action.payload.endDate);
      return { ...state, ...action.payload, errors: { ...errors } };
    }
    case 'defaultSelected':
      return { ...state, ...action.payload };
    case 'dateRangeChanged': {
      const errors = verifyRange(state.minDate, state.maxDate, action.payload.startDate, action.payload.endDate);
      const updates = getSelectValuesForDates(state.predefinedRanges, action.payload.startDate, action.payload.endDate);
      return { ...state, ...updates, errors: { ...errors }, rangePosition: action.payload.rangePosition };
    }
    case 'inputFocus': {
      return { ...state, rangePosition: action.payload };
    }
    default:
      return state;
  }
}

const QDateRangePicker = (props) => {
  const {
    id,
    onSelect,
    allowCustomRange,
    compact,
    disablePastDates,
    disableFutureDates,
    popoverProps,
    renderInput,
    ...rest
  } = props;
  const initialState = useMemo(() => initState(rest), [rest]);
  const [state, dispatch] = useReducer(reducer, initialState);

  // Sync state with selectedRangeId prop changes
  React.useEffect(() => {
    if (rest.selectedRangeId) {
      const [startDate, endDate] = getDatesForId(state.shortCutsMap, rest.selectedRangeId) || [undefined, undefined];
      const displayedRange = getDisplayedRangeForId(state.predefinedRanges, rest.selectedRangeId);
      dispatch({
        type: 'shortcutSelected',
        payload: {
          startDate,
          endDate,
          selectedShortcutId: rest.selectedRangeId,
          displayedRange,
        },
      });
    }
    // Only run when selectedRangeId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.selectedRangeId]);

  const buttonRef = useRef();
  const { classes, cx } = useStyles();
  const keyboardButtonProps = useMemo(() => ({ classes: { root: classes.clearDateIcon } }), [classes.clearDateIcon]);

  const handleDateSelection = ([startDate, endDate]) => {
    // force the focus to the start date if it's missing
    const rangePosition = !startDate ? 'start' : 'end';
    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition } });
  };

  const handleShortcutSelect = (selectedShortcutId) => {
    if (selectedShortcutId) {
      const [startDate, endDate] = getDatesForId(state.shortCutsMap, selectedShortcutId);
      const displayedRange = selectedShortcutId
        ? getDisplayedRangeForId(state.predefinedRanges, selectedShortcutId)
        : 'Custom';
      dispatch({ type: 'shortcutSelected', payload: { startDate, endDate, selectedShortcutId, displayedRange } });

      if (!allowCustomRange) {
        onSelect({
          id: state.id,
          start: startDate,
          end: endDate,
          interval: Interval.fromDateTimes(startDate, endDate),
          selectedRangeId: selectedShortcutId,
          defaultRangeId: state.selectedDefaultId,
        });
      }
    }
  };

  const handleAccept = () => {
    dispatch({ type: 'calendarDisplayed', payload: null });

    onSelect({
      id: state.id,
      start: state.startDate,
      end: state.endDate,
      interval: Interval.fromDateTimes(state.startDate, state.endDate),
      selectedRangeId: state.selectedShortcutId,
      defaultRangeId: state.selectedDefaultId,
    });
  };

  const handleClose = () => {
    dispatch({ type: 'cancel', payload: initialState });

    onSelect({
      id: state.id,
      start: initialState.startDate,
      end: initialState.endDate,
      selectedRangeId: initialState.selectedShortcutId,
      defaultRangeId: initialState.selectedDefaultId,
    });
  };

  const handleDefaultSelect = (selectedDefaultId) =>
    selectedDefaultId && dispatch({ type: 'defaultSelected', payload: { selectedDefaultId } });

  const handleInputFocus = (event) => event.target.select();

  const handleInputChange = (input, value) => {
    const startDate = input === 'start' ? value : state.startDate;
    const endDate = input === 'end' ? value : state.endDate;
    const rangePosition = input === 'start' ? 'end' : 'start';

    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition } });
  };

  const handleInputClear = (input) => {
    const startDate = input === 'start' ? null : state.startDate;
    const endDate = input === 'end' ? null : state.endDate;
    const rangePosition = !startDate && !endDate ? 'start' : input;
    dispatch({ type: 'dateRangeChanged', payload: { startDate, endDate, rangePosition } });
  };

  const dateRangeString = () =>
    `${DateTime.fromISO(state.startDate).toLocaleString(DateTime.DATE_MED)} - ${DateTime.fromISO(
      state.endDate,
    ).toLocaleString(DateTime.DATE_MED)}`;

  const shouldDisableDate = (date) =>
    (state.minDate && date < state.minDate) || (state.maxDate && date > state.maxDate);

  const showCalendar = (e) => dispatch({ type: 'calendarDisplayed', payload: e?.target });

  const renderDefaultInput = (onClickHandler) => (
    <QButton
      onClick={onClickHandler}
      ref={buttonRef}
      className={cx(classes.buttonSelect, compact && 'compact')}
      startIcon={<DateRangeIcon />}
      endIcon={<KeyArrowDownIcon />}
    >
      {compact && state.displayedRange === 'Custom' ? dateRangeString() : state.displayedRange}
    </QButton>
  );

  return (
    <Box className={classes.dateRangeContainer}>
      {allowCustomRange ? (
        <>
          {renderInput ? renderInput(showCalendar) : renderDefaultInput(showCalendar)}

          <Popover
            id={id || 'date-range-picker-popover'}
            open={Boolean(state.anchorEl)}
            anchorEl={state.anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            {...popoverProps}
            onClose={handleClose}
          >
            <div className={classes.root}>
              <StaticDateRangePicker
                shouldDisableDate={shouldDisableDate}
                value={[state.startDate, state.endDate]}
                onChange={handleDateSelection}
                disablePast={disablePastDates}
                disableFuture={disableFutureDates}
                rangePosition={state.rangePosition}
                slots={{
                  shortcuts: state.shortCutsMap.size ? CustomShortcuts : null,
                  layout: CustomLayout,
                  actionBar: CustomActionbar,
                  toolbar: CustomToolbar,
                }}
                slotProps={{
                  shortcuts: {
                    shortcutItems: state.predefinedRanges,
                    selectedShortcutId: state.selectedShortcutId,
                    handleShortcutSelect,
                  },
                  actionBar: {
                    shortcutItems: state.predefinedRanges,
                    selectedDefaultId: state.selectedDefaultId,
                    handleAccept,
                    handleClose,
                    handleDefaultSelect,
                    isValidRange: state.errors.isValidRange,
                  },
                  toolbar: {
                    startDate: state.startDate,
                    endDate: state.endDate,
                    handleInputChange,
                    handleInputFocus,
                    handleInputClear,
                    keyboardButtonProps,
                    errors: state.errors,
                  },
                }}
                calendars={2}
              />
            </div>
          </Popover>
        </>
      ) : (
        <ShortcutsSelect
          shortcutItems={state.predefinedRanges}
          selectedId={state.selectedShortcutId}
          handleSelect={handleShortcutSelect}
        />
      )}

      {!compact && (
        <Typography variant='bodyRegular' className={classes.dateRangeType}>
          {dateRangeString()}
        </Typography>
      )}
    </Box>
  );
};

QDateRangePicker.propTypes = {
  id: PropTypes.string,
  startingDate: PropTypes.object,
  endingDate: PropTypes.object,
  popoverProps: PropTypes.object,
  ranges: PropTypes.array,
  selectedRangeId: PropTypes.string,
  defaultRangeId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  disablePastDates: PropTypes.bool,
  disableFutureDates: PropTypes.bool,
  maxDaysBefore: PropTypes.number,
  maxDaysAfter: PropTypes.number,
  allowCustomRange: PropTypes.bool, // if false, show ranges as a dropdown.  if true, show calendars to allow selection
  compact: PropTypes.bool, // use a compact input rendering
  renderInput: PropTypes.func,
};

export default QDateRangePicker;
